import { readFileSync } from 'fs';
import EditorLayout, {
  CodeEditor,
  PanePage,
  PaneEditor,
  Iframe,
} from '../lib/components/editor';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { gql, useQuery } from '@apollo/client';
// import { context as contextResolver } from '../../lib/graphql/context';
// import { initializeApollo } from '../../lib/graphql/apollo';
import { Meta, Spinner } from '../lib/ui';
import { useAuth } from '../lib/auth';
import { Save, Download, Share, Send, DarkTheme, InspectElement } from '../lib/components/editor/actions';
import starterCss from '../lib/landing-page-templates/css';
import starterHtml from '../lib/landing-page-templates/html';

type Props = {
  starterHtml: string;
  starterCss: string;
};

const isServer = typeof window === 'undefined';

const filters = `img, video {
    filter: invert(1) hue-rotate(180deg) !important;
}`

// TODO: store template in localstorage
const LandingPage = (_props: Props) => {
  const router = useRouter();
  const { user } = useAuth();
  const htmlRef = useRef(null);
  const cssRef = useRef(null);
  const saveButton = useRef(null);
  const allowNextNavigationRef = useRef(false);
  const [htmlVal, setHtml] = useState(starterHtml);
  const [cssVal, setCss] = useState(starterCss);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isHtmlLoaded, setHtmlLoaded] = useState(false);
  const [isCssLoaded, setCssLoaded] = useState(false);
  const [isInitValueSet, setInitValue] = useState(false);
  const [darkTheme ,setDarkTheme] = useState(false)
  const [inspectMode, setInspectMode] = useState(false)

  useEffect(() => {
    if (!isInitValueSet && !user) {
      setInitValue(true);
    }
  }, [isInitValueSet, user]);
  // has changes
  useEffect(() => {
    if (starterHtml === htmlVal && starterCss === cssVal) {
      return setHasUnsavedChanges(false);
    }
    if (!hasUnsavedChanges && isInitValueSet) {
      setHasUnsavedChanges(true);
    }
  }, [
    starterCss,
    starterHtml,
    cssVal,
    htmlVal,
    hasUnsavedChanges,
    isInitValueSet,
  ]);
  useEffect(() => {
    const confirmationMessage =
      'The last changes you made to this template have not been saved. Are you sure you want to navigate away?';
    const beforeUnloadHandler = (e: BeforeUnloadEvent) => {
      (e || window.event).returnValue = confirmationMessage;
      return confirmationMessage; // Gecko + Webkit, Safari, Chrome etc.
    };
    const shouldAllowNavigation = () => confirm(confirmationMessage);
    const clickHandler = (event: MouseEvent) => {
      if (event.defaultPrevented) {
        return;
      }
      if (
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }
      const target = event.target as Element | null;
      const anchor = target?.closest('a') as HTMLAnchorElement | null;
      if (!anchor) {
        return;
      }
      if (anchor.hasAttribute('download') || (anchor.target && anchor.target !== '_self')) {
        return;
      }
      const href = anchor.getAttribute('href');
      if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) {
        return;
      }
      const url = new URL(anchor.href, window.location.href);
      if (url.origin !== window.location.origin) {
        return;
      }
      const currentUrl = new URL(window.location.href);
      if (
        url.pathname === currentUrl.pathname &&
        url.search === currentUrl.search &&
        url.hash === currentUrl.hash
      ) {
        return;
      }
      if (!shouldAllowNavigation()) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      allowNextNavigationRef.current = true;
    };
    const originalPush = router.push;
    const originalReplace = router.replace;
    const wrapNavigation = (method: typeof router.push) => {
      return (...args: Parameters<typeof router.push>) => {
        if (allowNextNavigationRef.current) {
          allowNextNavigationRef.current = false;
          return method(...args);
        }
        if (!shouldAllowNavigation()) {
          return Promise.resolve(false);
        }
        return method(...args);
      };
    };
    if (hasUnsavedChanges) {
      window.addEventListener('beforeunload', beforeUnloadHandler);
      document.addEventListener('click', clickHandler, true);
      router.beforePopState(() => shouldAllowNavigation());
      router.push = wrapNavigation(originalPush) as typeof router.push;
      router.replace = wrapNavigation(originalReplace) as typeof router.replace;
    } else {
      window.removeEventListener('beforeunload', beforeUnloadHandler);
      document.removeEventListener('click', clickHandler, true);
      router.beforePopState(() => true);
      router.push = originalPush;
      router.replace = originalReplace;
    }
    return () => {
      window.removeEventListener('beforeunload', beforeUnloadHandler);
      document.removeEventListener('click', clickHandler, true);
      router.beforePopState(() => true);
      router.push = originalPush;
      router.replace = originalReplace;
    };
  }, [hasUnsavedChanges]);
  function onHtmlMount(editor, monaco) {
    htmlRef.current = editor;
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KEY_S, () => {
      if (saveButton.current) {
        saveButton.current.click();
      }
    });
    setHtmlLoaded(true);
  }
  function onCssMount(editor, monaco) {
    cssRef.current = editor;
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KEY_S, () => {
      if (saveButton.current) {
        saveButton.current.click();
      }
    });
    setCssLoaded(true);
  }

  const save = (
    <Save
      buttonRef={saveButton}
      hasChanges={hasUnsavedChanges}
      data={{ rawTemplate: htmlVal, css: cssVal, id: 'starter' }}
    />
  );
  const download = (
    <Download
      isPublic={true}
      data={{
        rawTemplate: htmlVal,
        css: cssVal,
        name: 'Postdrop',
      }}
    />
  );

  const darkModeSetting = <DarkTheme darkTheme={darkTheme} setDarkTheme={setDarkTheme} key={"dark-theme"}/>

  const handleElementSelect = useCallback((lineNumber: number) => {
    if (!htmlRef.current) return;
    const editor = htmlRef.current;
    editor.revealLineInCenter(lineNumber);
    editor.setPosition({ lineNumber, column: 1 });
    editor.focus();
  }, []);

  const inspectSetting = (
    <InspectElement
      inspectMode={inspectMode}
      setInspectMode={setInspectMode}
      key={"inspect-element"}
    />
  );

  const share = (
    <Share data={{ id: 'starter', rawTemplate: htmlVal, css: cssVal }} />
  );
  const send = (
    <Send data={{ id: 'starter', rawTemplate: htmlVal, css: cssVal }} />
  );
  if (user) {
    router.push('/templates');
    return null;
  }
  return (
    <EditorLayout
      subtitle={`Inline, send and test HTML email templates`}
      save={save}
      download={download}
      share={share}
      darkMode={darkModeSetting}
      inspect={inspectSetting}
      send={send}
      publicDraft={{ rawTemplate: htmlVal, css: cssVal }}
      onRequestSave={() => saveButton.current?.click()}>
      <Meta title={`Inline, send and test HTML email templates`} />
      <PanePage>
        <PaneEditor>
          <CodeEditor
            onMount={onHtmlMount}
            value={htmlVal}
            onChange={setHtml}
            language="html"
            loaded={isHtmlLoaded}
          />
          <CodeEditor
            onMount={onCssMount}
            value={cssVal}
            onChange={setCss}
            language="css"
            loaded={isCssLoaded}
          />
        </PaneEditor>
        <div className={`bg-white flex-1 h-full w-full flex flex-col ${darkTheme ? 'dark-theme-filter' : ''}`}>
          <Iframe
            src={htmlVal}
            css={`${darkTheme ? filters : ''} ${cssVal}`}
            sandbox="allow-popups allow-popups-to-escape-sandbox"
            className="bg-white flex-1 h-full w-full"
            inspectMode={inspectMode}
            onElementSelect={handleElementSelect}
          />
        </div>
      </PanePage>
    </EditorLayout>
  );
};

export default LandingPage;
